import { useTiming } from '@/hooks/useTiming';
import { useProjectIdb } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useProjectIdb';
import { getPresenter } from '@/pages/AdvancedEditor/hooks/useGenerateScenes';
import { getSceneScript } from '@/pages/AdvancedEditor/utils';
import { getUploadUrl, updateVideo } from '@/services/api';
import { hashCode } from '@/utils/utils';
import { LibraryItemType } from '@d-id/types';
import debounce from 'lodash/debounce';
import { when } from 'mobx';
import { StoreType } from 'polotno/model/store';
import { useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAdvancedEditorContext } from '../../context/useAdvancedEditorContext';
import { newProjectId, ProjectAutoSaveStatus } from '../../types';
import { getStoreProperty, PolotnoStoreKeys, usePolotnoState } from '../polotno-store';
import { useCreateProjectLayers } from '../useCreateProjectLayers';
import { createS3Artifacts, uploadS3Artifacts } from './artifacts';
import { verifyCredentialsAndRefreshIfNeeded } from './verifyCredentials';

export const GET_UPLOAD_URL = 'GET_UPLOAD_URL';
const DEBOUNCE_DELAY = 2000;
export const REFRESH_INTERVAL = 2 * 60 * 1000;

export const getStoreHash = (store: StoreType) => {
    const rawJson = store.toJSON();
    const rawJsonString = JSON.stringify(rawJson);
    return hashCode(rawJsonString);
};

export const useSaveProjectArtifacts = (projectId: string) => {
    const { isGenerating } = useAdvancedEditorContext();
    const queryClient = useQueryClient();
    const { saveIdbDraft } = useProjectIdb();
    const [projectAutoSaveStatus, setProjectAutoSaveStatus] = usePolotnoState(PolotnoStoreKeys.projectAutoSaveStatus);
    const { withTiming, trace } = useTiming();

    const hashRef = useRef(0);
    const { data, isFetched } = useQuery([GET_UPLOAD_URL, projectId], () => getUploadUrl(projectId), {
        refetchInterval: REFRESH_INTERVAL,
        staleTime: REFRESH_INTERVAL,
        cacheTime: REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        enabled: projectId !== '' && projectId !== newProjectId,
    });
    const { uploadProjectLayers } = useCreateProjectLayers(projectId);

    const abortControllerRef = useRef<AbortController | null>(null);
    const idleTimeout = useRef<NodeJS.Timeout | null>(null);

    const saveArtifacts = async (store: StoreType, shouldSkipHashCheck: boolean) => {
        if (isGenerating.current) {
            return;
        }

        if (!store.pages?.length || !store.pages[0]?.id) {
            return;
        }

        if (isFetched && data) {
            const rawJson = store.toJSON();
            const newHash = getStoreHash(store);

            if (!shouldSkipHashCheck && newHash === hashRef.current) {
                trace('saveArtifacts', { message: 'Skipping saving artifacts because hashes are the same' });
                return;
            }

            trace('saveArtifacts', {
                message: `Saving artifacts, shouldSkipHashCheck: ${shouldSkipHashCheck}, hash is different: ${newHash !== hashRef.current}`,
            });

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            hashRef.current = newHash;

            // TODO: remove storeScenes and layers after the /generate endpoint will handle the scenes
            await uploadProjectLayers(store);

            const updateProjectPromise = withTiming('updateVideo', () => updateVideo(projectId, {}, LibraryItemType.PROJECT));

            const { rawFile, thumbnailFile } = await withTiming('createS3Artifacts', () => createS3Artifacts(store, rawJson));

            const { raw: rawCreds, thumbnail: thumbnailCreds } = await verifyCredentialsAndRefreshIfNeeded({
                data,
                projectId,
                queryClient,
            });

            await Promise.all([
                withTiming('uploadS3Artifacts', () =>
                    uploadS3Artifacts(rawCreds, thumbnailCreds, rawFile, thumbnailFile, abortControllerRef.current!.signal)
                ),
                updateProjectPromise,
            ]);
        } else if (projectId === newProjectId) {
            const videoName = getStoreProperty(store, PolotnoStoreKeys.creativeVideoName);
            const subtitlesActive = getStoreProperty(store, PolotnoStoreKeys.subtitlesActive);
            const scripts = store.pages.map(page => getSceneScript(page, !!subtitlesActive));
            const presenters = store.pages.map(page => getPresenter(page));

            await saveIdbDraft({
                createdAt: new Date().toISOString(),
                store: store.toJSON(),
                videoName,
                presenters,
                id: projectId,
                scripts,
            });
        }
    };

    const saveArtifactsWrapper = async (store: StoreType) => {
        if (idleTimeout.current) {
            clearTimeout(idleTimeout.current);
        }

        try {
            const shouldSkipHashCheck = projectAutoSaveStatus === ProjectAutoSaveStatus.Error;

            setProjectAutoSaveStatus(ProjectAutoSaveStatus.Saving);
            await saveArtifacts(store, shouldSkipHashCheck);
            setProjectAutoSaveStatus(ProjectAutoSaveStatus.Saved);
            idleTimeout.current = setTimeout(() => {
                setProjectAutoSaveStatus(ProjectAutoSaveStatus.Idle);
            }, 1000);
        } catch (e) {
            setProjectAutoSaveStatus(ProjectAutoSaveStatus.Error);
            throw e;
        }
    };

    const debouncedSaveArtifacts = useCallback(debounce(saveArtifactsWrapper, DEBOUNCE_DELAY, { leading: false, trailing: true }), [
        data,
        isFetched,
    ]);

    // This function is used to save the project artifacts instantly.
    // The function is waiting for previous save to finish, cancelling the debounced save and then saving the artifacts.
    const instantSaveArtifacts = async (store: StoreType, shouldSkipHashCheck: boolean = true) => {
        await when(
            () => store?.['temporary' as unknown as 'custom']?.[PolotnoStoreKeys.projectAutoSaveStatus] !== ProjectAutoSaveStatus.Saving
        );
        debouncedSaveArtifacts.cancel();

        await saveArtifacts(store, shouldSkipHashCheck);
    };

    return { debouncedSaveArtifacts, saveArtifacts: instantSaveArtifacts, hash: hashRef.current };
};
